'use client';

import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { KeyboardEventHandler, useEffect, useRef } from 'react';

import type { FiatCurrencyCode } from '@/constants/fiatCurrency';
import { SUPPORTED_FIAT_CURRENCIES } from '@/constants/fiatCurrency';

import {
  useCurrencyStore,
  selectedCurrencySelector,
  setSelectedCurrencySelector,
} from '@/store/currency';

import { fontStyles } from '@/styles/typography';

import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { ChevronLeftIcon } from '@/ui/Icons/ChevronLeftIcon';

import { IDENTIFY_TYPES, identifyUserProperties } from '@/utils/amplitude';

interface CurrencySelectorPageProps {
  onGoBack: (e: React.MouseEvent | React.KeyboardEvent) => void;
}

export function CurrencySelectorPage({ onGoBack }: CurrencySelectorPageProps) {
  const selectedCurrency = useCurrencyStore(selectedCurrencySelector);
  const setSelectedCurrency = useCurrencyStore(setSelectedCurrencySelector);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const toggleGroupRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  const onChangeHandler = (newVal: string) => {
    if (!newVal) return;
    setSelectedCurrency(newVal as FiatCurrencyCode);
    identifyUserProperties({
      type: IDENTIFY_TYPES.SET,
      key: 'fiat_currency',
      value: newVal,
    });
  };

  const buttonKeyDownHandler: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key === 'ArrowDown') {
      const selected =
        toggleGroupRootRef.current?.querySelector<HTMLButtonElement>('button[tabindex="0"]') ??
        toggleGroupRootRef.current?.querySelector<HTMLButtonElement>('button');
      selected?.focus();
    }
  };

  const groupKeyDownHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    const firstButton = e.currentTarget.querySelector<HTMLButtonElement>('button');
    if (e.key === 'ArrowUp' && firstButton?.getAttribute('tabindex') === '0') {
      buttonRef.current?.focus();
    }
  };

  return (
    <div>
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: [
            'text-token-gray-1600',
            'font-semibold',
            'p-1',
            'flex',
            'mx-0.5',
            'mt-0.5',
            'items-center',
            'gap-1',
            'w-full',
            'rounded-md',
          ],
        })}
      >
        <button
          ref={buttonRef}
          className="text-token-gray-1600 rounded-sm w-7 h-7 flex items-center justify-center hover:bg-token-gray-200 focus-visible:bg-token-gray-200 focus-visible:outline-none"
          onKeyDown={buttonKeyDownHandler}
          aria-label="Go back"
          onClick={onGoBack}
        >
          <ChevronLeftIcon />
        </button>
        Currency
      </div>
      <div className="max-h-[380px] overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-token-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full">
        <ToggleGroup.Root
          ref={toggleGroupRootRef}
          className="flex flex-col p-0.5 text-token-gray-1600"
          type="single"
          loop={false}
          value={selectedCurrency}
          onKeyDown={groupKeyDownHandler}
          onValueChange={onChangeHandler}
        >
          {SUPPORTED_FIAT_CURRENCIES.map(({ code, name }) => (
            <ToggleGroup.Item
              key={code}
              value={code}
              className={fontStyles({
                variant: 'BODY_MD',
                class:
                  'pl-2.5 pr-4 py-2 flex flex-row items-center gap-3 rounded-md relative text-current hover:bg-token-gray-200',
              })}
            >
              <span className="w-8 text-center font-mono text-[11px] text-token-gray-800">
                {code}
              </span>
              {name}
              {selectedCurrency === code && (
                <CheckmarkIcon className="absolute top-0 bottom-0 my-auto right-3 text-token-gray-800" />
              )}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      </div>
    </div>
  );
}
